// core
import { Injectable } from '@angular/core';
import moment from 'moment';

// coonstants

// services
import { ConfigService, LoggerService, PolicyService, ContentService } from '@campfire-nx/api';
import { ProductDetailsService } from '@open-portal-app/core/services/product-details.service';
import { PolicyExtrasService } from '@open-portal-app/core/services/policy-extras.service';
import { PolicyPersonnelService } from '@open-portal-app/core/services/policy-personnel.service';
import { PolicyClaimsService } from '@open-portal-app/core/services/policy-claims.service';
import { PolicyDocumentService } from '@open-portal-app/core/services/policy-document.service';

// pipes
import { AddressPipe } from '@open-portal-app/core/pipes/address.pipe';
import { environment } from '@open-portal-env';
import { HOME_CONSTANTS } from '../constants/home.constants';
import { CAR_CONSTANTS } from '../constants/car.constants';
import { POLICY_EXCESS, POLICY_DETAIL_CONFIG } from '../constants/policy.constants';

// interface
import { IConfigInterface } from '@open-portal-app/policy/components/policy-link-cards/policy-link-cards.component';

export interface IExtraInterface {
  charge_upfront: boolean,
  description: string,
  is_included: boolean,
  label: string
}

interface Excess {
  name: string;
  key: string;
}

interface ExcessDetail {
  name: string;
  summary: string;
}

@Injectable({
  providedIn: 'root'
})
export class PolicyDetailsService {
  policy!: any;
  formattedPolicy: any = {};
  productDetails!: any;
  isRaptorial!: boolean;
  apiEndpoint?: string = environment.apiEndpoint;

  constructor(
    private productDetailsService: ProductDetailsService,
    private configService: ConfigService,
    private policyService: PolicyService,
    private loggerService: LoggerService,
    private addressPipe: AddressPipe,
    private policyExtrasService: PolicyExtrasService,
    private policyPersonnelService: PolicyPersonnelService,
    private policyClaimsService: PolicyClaimsService,
    private policyDocumentService: PolicyDocumentService,
    private contentService: ContentService
  ){}

  getLegacyPolicyDetails(productClass: string, policySlug: string) {
    return this.policyService.getLegacyPolicyBySlug(productClass, policySlug)
      .then((response: any) => {
        this.policy = response;

        return response;
      })
  }

  getPolicyDetails(policySlug: string) {
    return this.policyService.getPolicyBySlug(policySlug)
      .then((response: any) => {
        this.policy = response;

        return response;
      })
  }

  // TODO: Will enhance for other kinds of policy updates
  //  Currently updates policy with payment details
  updatePolicyDetails(payload: any, isRaptorial: boolean, productClass: string) {
    return new Promise((resolve, reject) => {
      if (isRaptorial) {
        this.policyService.updatePaymentByPolicySlug(payload.slug, payload)
          .then((response: any) => {
            resolve(response);
          })
      } else {
        this.policyService.updateLegacyPolicyBySlug(productClass || '', payload.slug, payload)
          .then((response: any) => {
            resolve(response);
          })
          .catch((error) => {
            this.loggerService.error(
              'PolicyDetailsService',
              'updatePolicyDetails',
              { error }
            );

            reject(error);
          })
      }
    })
  }

  formatPolicy(policyDetails?: { [key: string]: any }, userData?: any) {
    const currentPolicy = policyDetails || this.policy;
    this.isRaptorial = currentPolicy.product.is_raptorial;

    return this.productDetailsService.get(currentPolicy.product.slug)
      .then((response: any) => {
        this.productDetails = response;

        switch (currentPolicy.product.class) {
          case 'car':
            return this.formatCarPolicy(currentPolicy, userData);
            break;
          case 'home':
            return this.formatHomePolicy(currentPolicy, userData);
            break;
          case 'travel':
            return this.formatTravelPolicy(currentPolicy, userData);
            break;
          case 'mobile':
            return this.formatMobilePolicy(currentPolicy, userData);
            break;
          default:
            // TODO: put a resolve
            return true;
        }
      });
  }

  async formatCarPolicy(policy: { [key: string]: any }, userData?: string) {
    const phase = !this.isRaptorial ? this.getCurrentPhase(policy.policys_car_phases) : null;
    const car = !this.isRaptorial ? phase.cars[0] : undefined;
    const rego = !this.isRaptorial ? car.rego.toUpperCase() : policy.data.vehicle_rego;
    const drivers = !this.isRaptorial ? car.drivers : this.getDrivers(policy);
    const policyClassLabel = 'Car Insurance';
    const paydLabel = 'Pay As You Drive Cover';
    let carPolicyTypeLabel = "";
    this.productDetails = this.productDetailsService.product;

    this.productDetails.covers?.forEach((cover: any) => {
      if (!this.isRaptorial && cover.policy_type === policy.policy_type || this.isRaptorial && cover.policy_type === policy.data.cover) {
          const label = this.formatCoverLabel(cover.label, null, paydLabel);
          carPolicyTypeLabel = label;
      }
    });

    let extraTemplate: any = [];
    if (policy.product.policy_detail_template) {
      const content = await this.contentService.getById(policy.product.policy_detail_template);
      extraTemplate = content && content.length > 0 ? content[0] : [];
    }

    const commonProperties = this.formatCommonProperties(policy, userData);
    const carProperties: any = {
      policyTypeLabel: !this.isRaptorial ? this.formatLegacyPolicyTypeLabel(policyClassLabel, carPolicyTypeLabel)
        : `${policy.human.cover}`,
      isMarketValue: !this.isRaptorial ? car.is_market_value : policy.human.is_agreed_value !== 'Agreed value',
      sumInsured: !this.isRaptorial ?
        car.is_market_value ?
          'Market value' : this.getPriceObject(policy, car.sum_insured) :
          this.getPriceObject(policy, policy.data.sum_insured),
      autoRenew: policy.renewal_flag === 'AR' || policy.renewal_flag === 'CE',
      mainDriver: this.getMainDriver(drivers)[0],
      authorisedPersons: this.policyPersonnelService.authorisedPersons(policy.authorised_persons, this.isRaptorial),
      additionalDrivers: this.getLegacyAdditionalDrivers(drivers),
      excess: !this.isRaptorial ? this.getPriceObject(policy, car.excess) : this.getPriceObject(policy, policy.data.excess),
      minAge: this.getMinAgeForVehicle(this.isRaptorial, policy, car),
      extras: !this.isRaptorial ? await this.policyExtrasService.getFormattedLegacyExtras(phase, extraTemplate) : this.policyExtrasService.getFormattedExtras(policy, extraTemplate),
      helpflowCarDisplayName: !this.isRaptorial ? `${car.nvic_obj.year} ${car.nvic_obj.fam.maker.name} ${car.nvic_obj.fam.name}`
        : `${policy.data.vehicle_year} ${policy.data.vehicle_make} ${policy.data.vehicle_model}`,
      carDisplayName: !this.isRaptorial ? `${car.nvic_obj.year} ${car.nvic_obj.fam.maker.name} ${car.nvic_obj.fam.name}`
        : `${policy.data.vehicle_year} ${policy.data.vehicle_make} ${policy.data.vehicle_model}`,
      carDetails: !this.isRaptorial ? `${car.nvic_obj.year} ${car.nvic_obj.fam.maker.name} ${car.nvic_obj.fam.name} ${car.nvic_obj.desc} ${car.nvic_obj.var}`
        : `${policy.data.vehicle_year} ${policy.data.vehicle_make} ${policy.data.vehicle_model} ${policy.data.vehicle_description}`,
      carRego: !this.isRaptorial ? rego : policy.data.vehicle_rego,
      finance: !this.isRaptorial ? this.getFinanceType(car.finance_type) : policy.human.finance_type,
      financeProvider: car?.current_financier || 'Not available',
      isPayd: (policy?.policy_type || policy.data?.cover) === CAR_CONSTANTS.carCoverTypes.comprehensiveFixedKm,
      kmsKilo: !this.isRaptorial ? car.kms_kilo : policy.data.kms_estimated / 1000,
      kmsKiloPayd: !this.isRaptorial ? car.kms_kilo_payd : policy.data.kms_estimated / 1000,
      paymentPlanData: !this.isRaptorial ? this.getPaymentPlanDetails(phase) : null,
      firstPayableInvoice: this.getPriceObject(policy, (!this.isRaptorial ? phase.payable_first_invoice : policy.premiums.payable_first_invoice)),
      isUpfront: this.getPaymentFrequency(policy).label === 'Annual',
      overdueInvoices: this.getDueInvoices(policy, phase),
      gstRate: policy.product?.gst_rate,
      coverType: !this.isRaptorial ? this.getLegacyCarCoverType(policy) : policy.data?.cover_type,
      coverLevel: !this.isRaptorial ? this.getLegacyCarCoverLevel(policy) : policy.data?.cover_level
    };

    carProperties.policyTitle = `${carProperties?.carRego} - ${carProperties?.carDisplayName}`;

    return Object.assign(commonProperties, carProperties);
  }

  async formatHomePolicy(policy: { [key: string]: any }, userData: any) {
    this.isRaptorial = policy.product.is_raptorial;
    const phase = !this.isRaptorial ? this.getCurrentPhase(policy.policys_home_phases) : undefined;
    const hasBuilding = !this.isRaptorial ? HOME_CONSTANTS.isBuilding(policy.policy_type)
      : !!policy.data.sum_insured_building;
    const hasContents = !this.isRaptorial ? HOME_CONSTANTS.isContents(policy.policy_type)
      : !!policy.data.sum_insured_contents;
    const isLandlords =
      phase && HOME_CONSTANTS.isLandlords(phase.dwelling);
    let homePolicyTypeLabel;
    this.productDetails = this.productDetailsService.product;

    let extraTemplate: any = [];
    if (policy.product.policy_detail_template) {
      const content = await this.contentService.getById(policy.product.policy_detail_template);
      extraTemplate = content && content.length > 0 ? content[0] : [];
    }

    if (hasBuilding && hasContents) {
      homePolicyTypeLabel = `${isLandlords ? 'Landlords Building ' : 'Home '  }& Contents Insurance`;
    }
    if (hasBuilding && !hasContents) {
      homePolicyTypeLabel = `${isLandlords ? 'Landlords Building ' : 'Home '  }Insurance`;
    }
    if (!hasBuilding && hasContents) {
      homePolicyTypeLabel = `${isLandlords ? 'Landlords ' : ''  }Contents Insurance`;
    }

    const commonProperties = this.formatCommonProperties(policy, userData);
    const homeProperties = {
      policyTypeLabel: !this.isRaptorial ? homePolicyTypeLabel || this.configService.data.types.home.policyTypes[policy.policy_type].label
        : `${policy.human.cover}`,
      autoRenew: policy.renewal_flag === 'AR' || policy.renewal_flag === 'CE',
      primaryPolicyHolder: !this.isRaptorial ? this.getLegacyPrimaryPolicyHolder(phase?.policyholders) : this.getPrimaryPolicyHolder(policy),
      additionalPolicyholders: !this.isRaptorial ? this.getAdditionalLegacyPolicyHolders(phase.policyholders)
        : this.getAdditionalPolicyHolders(policy),
      authorisedPersons: this.policyPersonnelService.authorisedPersons(policy.authorised_persons, this.isRaptorial),
      hasBuilding,
      hasContents,
      isLandlords,
      excess: {
        building: this.getPriceObject(
          policy,
          !this.isRaptorial ? phase.building_excess
            : policy.human.excess_building || policy.data.excess_building
        ),
        contents: this.getPriceObject(
          policy,
          !this.isRaptorial ? phase.contents_excess
          : policy.human.excess_contents || policy.data.excess_contents
        ),
      },
      sumInsured: {
        building: this.getPriceObject(
          policy,
          !this.isRaptorial ? phase.building_value
            : policy.human.sum_insured_building || policy.data.sum_insured_building
        ),
        contents: this.getPriceObject(
          policy,
          !this.isRaptorial ? phase.contents_value
            : policy.human.sum_insured_contents || policy.data.sum_insured_contents
        ),
      },
      extras: !this.isRaptorial ? await this.policyExtrasService.getFormattedLegacyExtras(phase, extraTemplate) : undefined,
      address: !this.isRaptorial ? phase.risk_address_obj
        : policy.human.risk_address || policy.data.risk_address,
      policyTitle: !this.isRaptorial ? this.addressPipe.transform(phase.risk_address_obj)
        : policy.human.risk_address || policy.data.risk_address,
      valuables: this.getValuables(policy, phase),
      firstPayableInvoice: this.getPriceObject(policy, (!this.isRaptorial ? phase.payable_first_invoice : policy.premiums.payable_first_invoice)),
      paymentPlanData: !this.isRaptorial ? this.getPaymentPlanDetails(phase) : undefined,
      overdueInvoices: this.getDueInvoices(policy, phase),
      gstRate: policy.product?.gst_rate,
      coverType: policy.data?.cover_type,
      coverLevel: policy.data?.cover_level
    };

    return Object.assign(commonProperties, homeProperties);
  }

  async formatTravelPolicy(policy: { [key: string]: any }, userData: any) {
    let extraTemplate: any = [];
    if (policy.product.policy_detail_template) {
      const content = await this.contentService.getById(policy.product.policy_detail_template);
      extraTemplate = content && content.length > 0 ? content[0] : [];
    }

    const commonProperties = this.formatCommonProperties(policy, userData);
    const travelProperties = {
      coverType: policy.data?.cover_type,
      coverLevel: policy.data?.cover_level,
      destinationTitle : this.getDestination(policy),
      policyTitle : this.getDestination(policy),
      policyTypeLabel: this.getTravelPolicyType(policy),
      departureDate: policy.data.trip_start_date,
      returnDate: policy.end_date,
      purchaseDate: policy.start_date,
      travelRegion: policy.human?.travel_region,
      travelRegionType: policy.data?.travel_region_worldwide_type,
      excess: this.getPriceObject(policy, policy.data?.excess),
      autoRenew: policy.renewal_flag === 'AR' || policy.renewal_flag === 'CE',
      primaryTraveller: {
        givenName: policy.data?.policy_holder_given_name,
        lastName: policy.data?.policy_holder_last_name
      },
      primaryTravellerDob: policy.data?.policy_holder_dob,
      additionalTravellers: policy.data?.additional_traveller_list,
      extras: this.policyExtrasService.getFormattedExtras(policy, extraTemplate),
      valuables: policy.data?.feature_valuable_list,
      dependants: policy.data?.dependent_traveller_list,
      overdueInvoices: this.getDueInvoices(policy)
    };

    return Object.assign(commonProperties, travelProperties);
  }

  formatMobilePolicy(policy: { [key: string]: any }, userData: any) {
    const commonProperties = this.formatCommonProperties(policy, userData);
    const mobileProperties = {
      coverType: policy.data?.cover_type,
      coverLevel: policy.data?.cover_level,
      policyTypeLabel: policy.human.cover,
      policyHolder: {
        givenName: policy.data?.policy_holder_given_name,
        lastName: policy.data?.policy_holder_last_name,
        gender: policy.data?.policy_holder_gender,
        dob: policy.data?.policy_holder_dob,
      },
      firstPayableInvoice: this.getPriceObject(policy, policy.premiums.payable_first_invoice),
      isUpfront: policy.frequency === 'U',
      excessBreakdown: this.getPriceObject(policy, policy.data.excess_breakdown),
      excessDamage: this.getPriceObject(policy, policy.data.excess_damage),
      excessLoss: this.getPriceObject(policy, policy.data.excess_loss),
      excessTheft: this.getPriceObject(policy, policy.data.excess_theft),
      excess: this.getExcessDetails(policy, POLICY_EXCESS.mobile),
      excessConfig: this.getExcessConfig(policy.data?.is_device_validated),
      autoRenew: policy.renewal_flag === 'AR' || policy.renewal_flag === 'CE',
      deviceVerified: policy.data?.is_device_validated,
      deviceModel: this.formatMobileDeviceTitle(policy.data.device),
      policyTitle: this.formatMobileDeviceTitle(policy.data.device),
      deviceImei: policy.data?.device_imei,
      overdueInvoices: this.getDueInvoices(policy),
    };

    return Object.assign(commonProperties, mobileProperties);
  }

  formatCommonProperties(policy: { [key: string]: any }, userData: any) {
    const isRaptorial = policy.product.is_raptorial;
    const policyDocuments: any = this.policyDocumentService.getPolicyDocuments(policy);
    
    const claims: { active: any[], completed: any[] } = {
      active: [],
      completed: []
    };

    if (userData?.claims) {
        const policySlug = policy.uuid || policy.slug;
        const policyClaims = userData.claims.filter((claim: any) => claim.policy_slug === policySlug);

        if (policyClaims && policyClaims.length > 0) {
            claims.active = this.policyClaimsService.updateClaimsList(policyClaims, 'active');
            claims.completed = this.policyClaimsService.updateClaimsList(policyClaims, 'completed');
        }
    }

    return {
      slug: !isRaptorial ? policy.slug : policy.uuid,
      policyNumber: policy.policy_number,
      policyState: policy.policy_state || policy.state_display,
      startDate: policy.start_date,
      endDate: policy.end_date,
      premium: this.getPremium(policy),
      paymentFrequency: this.getPaymentFrequency(policy),
      isRaptorial: this.isRaptorial,
      productClass: policy.product.class,
      partnerSlug: policy.partner?.slug,
      userSlug: policy.user_slug || policy.user?.uuid,
      userName: policy.user ? `${policy.user.first_name} ${policy.user.last_name}` : '',
      documents: policyDocuments,
      claims: claims,
      fnolThirdParty: policy.product?.tpa_settings?.use_tpa,
      icon: this.getProductIcon(policy.product.class),
      desktopBannerImage: policy.product.banner_desktop !== null ? `${this.apiEndpoint}/media/${policy.product.banner_desktop}` : null,
      mobileBannerImage: policy.product.banner_mobile !== null ? `${this.apiEndpoint}/media/${policy.product.banner_mobile}` : null,
      policyViewV2: policy.partner.policy_view_v2,
      fnolViaIntercom: policy.product?.tpa_settings?.start_via_intercom,
      fnolTPAs: policy.product?.tpa_settings?.tpas,
      rawExtras: this.isRaptorial ? policy.extras : null
    };
  }

  /** Functions used for policy formatting across products */
  getCurrentPhase(phases: Array<any>, strict = false) {
    let currentPhase: any = phases.filter((phase) => phase.is_current_phase);

    // For cancelled or expired policy there may be no phase with 'is_current_phase === true'
    if (!strict && (!currentPhase || currentPhase.length === 0)) {
      // Sort by id,desc. Choose largest/latest id from index 0
      currentPhase = phases.sort((a, b) => parseInt(b.id) - parseInt(a.id));
    }
    return currentPhase[0];
  }

  getPaymentPlanDetails(phase: any) {
    if (!phase.payment_plan_data) {
      return;
    }

    let promotionMessage = '';

    if (phase.payment_plan_data.is_nmonthsfree) {
      const { params } = phase.payment_plan_data;
      const monthMessage = `First ${params.months_free > 1 ? `${params.months_free  } months` : "month"}`
      const chargeAtPosMessage = `${params.charge_at_pos === 0 ? "FREE" : `for $${  params.charge_at_pos}`}`

      promotionMessage = `${monthMessage} ${chargeAtPosMessage}`;
    }

    return {
      isNMonthsFree: phase.payment_plan_data.is_nmonthsfree,
      promotionMessage,
    };
  }

  getDueInvoices(policy: any, phase?: any) {
    let policyInvoices: any = {
      count: 0,
      invoices: []
    };

    if (phase) {
      // legacy policies
      policyInvoices.onAccount = (phase.paying_partner_id !== undefined && phase.paying_partner_id !== null);
      if (!policyInvoices.onAccount && phase.invoices) {
        policyInvoices.invoices = phase.invoices.filter((invoice: any) => invoice.is_due && !invoice.is_settled);
        policyInvoices.count = policyInvoices.invoices && policyInvoices.invoices.length;
      }
    } else {
      // raptor policies
      policyInvoices.onAccount = (policy.paying_partner_id !== undefined && policy.paying_partner_id !== null);
      if (!policyInvoices.onAccount && policy.invoices) {
        policyInvoices.invoices = policy.invoices.filter((invoice: any) => invoice.is_due && !invoice.is_settled);
        policyInvoices.count = policyInvoices.invoices && policyInvoices.invoices.length;
      }
    }

    return policyInvoices;
  }

  getPremium(policy: any) {
    let premium = '';

    if (this.isRaptorial) {
      // keeping Portal v1's formattedPayment for payment related info in the future
      // const { payment_method } = policy;
      // let formattedPayment = {
      //   frequency: policy.frequency,
      //   yearly: policy.premiums.written_total,
      //   monthly: policy.premiums.written_monthly,
      //   payment_method: Object.assign(payment_method, {
      //     isExpired: payment_method
      //       ? moment(payment_method.expires_on).diff(moment(), 'days') < 0
      //       : false,
      //   }),
      //   showCardPayment: true
      // };

      premium = policy.frequency === 'M' ? policy.premiums.written_monthly : policy.premiums.written_total;
    } else {
      let phase;
      const productClass = policy.product.class;

      if (productClass === 'car') {
        phase = this.getCurrentPhase(policy.policys_car_phases);
      } else if (productClass === 'home') {
        phase =  this.getCurrentPhase(policy.policys_home_phases);
      }

      premium = phase?.frequency === 'M' ? phase?.premium_monthly : phase?.premium;
    }

    return this.getPriceObject(policy, premium);
  }

  getPaymentFrequency(policy: any) {
    let frequency = '';
    let phase;

    if (this.isRaptorial) {
      frequency = policy.frequency === 'M' ? 'Monthly' : 'Annual';
    } else {
      const productClass = policy.product.class;

      if (productClass === 'car') {
        phase = this.getCurrentPhase(policy.policys_car_phases);
      } else if (productClass === 'home') {
        phase =  this.getCurrentPhase(policy.policys_home_phases);
      }

      frequency = phase?.frequency === 'M' ? 'Monthly' : 'Annual';
    }

    return { key: policy.frequency || phase?.frequency, label: frequency };
  }

  getPriceObject(policy: any, value: any) {
    let currency = '$';
    if (
      policy.product &&
      policy.product.country &&
      policy.product.country.currency_symbol
    ) {
      currency = policy.product.country.currency_symbol;
    }

    // If value already has a currency symbol attached, remove it.
    if (typeof value === 'string' && value.indexOf(currency) > -1) {
      value = value.replace(currency, '')
    }

    return {
      currency: currency,
      amount: value
    };
  }

  /** Car policy specific functions */

  getCar(policy: any) {
    return {
      rego: policy.data.vehicle_rego,
      year: policy.data.vehicle_year,
      description: policy.data.vehicle_description,
      family: policy.data.vehicle_model,
      make: policy.data.vehicle_make,
    }
  }

  getDrivers(policy: any) {
    let drivers = [];

    if (policy.data.has_other_driver) {
      drivers = policy.data.other_driver_list.map((driver: any) => ({
          first_name: driver.other_driver_given_name,
          last_name: driver.other_driver_last_name,
          dob: driver.other_driver_dob,
          driver_type: null,
          gender: driver.human_other_driver_gender,
          age_in_years: moment().diff(moment(driver.other_driver_dob), 'years')
        }));
    }

    drivers.unshift({
      is_primary: true,
      first_name: policy.data.policy_holder_given_name,
      last_name: policy.data.policy_holder_last_name,
      dob: policy.data.policy_holder_dob,
      gender: policy.human.policy_holder_gender,
      driver_type: 'P',
      age_in_years: moment().diff(moment(policy.data.policy_holder_dob), 'years')
    });

    return drivers;
  }

  getMainDriver(drivers: Array<any>) {
    return drivers.filter(driver => driver.driver_type === 'P');
  }

  getLegacyAdditionalDrivers(drivers: Array<any>) {
    return drivers.filter(driver => driver.driver_type !== 'P');
  }

  getFinanceType(financeType: number) {
    let returnFinance = {} as { type: number, label: unknown };

    for (const [key, value] of Object.entries(this.configService.data.types.carFinanceTypes)) {
      if (parseInt(key) === financeType) {
        returnFinance = {
          type: parseInt(key),
          label: value
        };
      }
    }

    return returnFinance;
  }

  // Show "Fixed Kilometer" in product name for quotes before April 1, 2021
  formatCoverLabel(coverLabel: string, policy: any, paydLabel: string) {
    const formattedLabel = coverLabel.replace( 'Pay As You Drive', paydLabel ).replace(' Cover', '').replace(' Plan', '');
    return formattedLabel;
  }

  formatLegacyPolicyTypeLabel(policyClassLabel: string, carPolicyTypeLabel: string | undefined) {
    return carPolicyTypeLabel? `${policyClassLabel} ${carPolicyTypeLabel}` : policyClassLabel;
  }

  formatMobileDeviceTitle(deviceModel: string) {
    let formattedTitle;
    // Strip underscores and capitalise
    formattedTitle = deviceModel.replace(/_/g, ' ').replace(/(^\w|\s\w)/g, m => m.toUpperCase());
    // Add special case for iPhone
    if (formattedTitle.includes('Iphone')) return `${formattedTitle.replace('Iphone', 'iPhone')}GB`;
    return `${formattedTitle}GB`;
  }

  /** Home policy specific functions */

  getPrimaryPolicyHolder(policy: any) {
    return [
      {
        first_name: policy.data.policy_holder_given_name,
        last_name: policy.data.policy_holder_last_name,
        dob: policy.data.policy_holder_dob,
        gender: policy.data.policy_holder_gender,
        mobile: policy.data.policy_holder_mobile,
        is_policyholder: true,
        is_primary: true,
      },
    ];
  }

  getLegacyPrimaryPolicyHolder(policyHolders: Array<any>) {
    return policyHolders.filter((policyHolder) => policyHolder.is_primary);
  }

  getAdditionalPolicyHolders(policy: any) {
    let policyholders = [];

    if (policy.data.other_policy_holder_list) {
      policyholders = policy.data.other_policy_holder_list.map(
        (policyholder: any) => ({
            first_name: policyholder.other_policy_holder_given_name,
            last_name: policyholder.other_policy_holder_last_name,
            dob: policyholder.other_policy_holder_dob,
            is_policyholder: true,
          })
      );
    }

    return policyholders;
  }

  getAdditionalLegacyPolicyHolders(policyHolders: Array<any>) {
    return policyHolders.filter((policyHolder) => !policyHolder.is_primary);
  }

  getValuables(policy: any, phase: any) {
    let valuables;

    if (this.isRaptorial) {
      valuables = policy.data.feature_valuables_list?.map(
        (valuable: any) => ({
            name: valuable.human_valuable_category || valuable.valuable_category,
            value: this.getPriceObject(policy, valuable.valuable_value)
          })
      );
    } else {
      valuables = phase.valuables?.map(
        (valuable: any) => ({
            name: valuable.name,
            value: this.getPriceObject(policy, valuable.value)
          })
      );
    }
    return valuables;
  }

  getResidents(policyHolders: Array<any>) {
    return policyHolders.filter(policyHolder => policyHolder.is_resident);
  }

  getCarDisplayName(policy: any, withRego = true) {
    const phase = !policy.product.is_raptorial ? this.getCurrentPhase(policy.policys_car_phases) : undefined;
    const car = !policy.product.is_raptorial ? phase.cars[0] : undefined;

    const displayName = !policy.product.is_raptorial
        ? `${car.nvic_obj.year} ${car.nvic_obj.fam.maker.name} ${car.nvic_obj.fam.name}`
        : `${policy.data.vehicle_year} ${policy.data.vehicle_make} ${policy.data.vehicle_model}`;

    return displayName + (withRego ? ` ${  !policy.product.is_raptorial ? car.rego.toUpperCase() : policy.data.vehicle_rego}` : '');
  }

  getMinAgeForVehicle(isRaptorial: boolean,  policy: any, car: any) {
    let minAge;
    const raptorPolicyMinAge = isRaptorial && policy.data?.exclude_under_25 ? 25 :
      policy.data?.vehicle_min_age_restriction;
    minAge = !this.isRaptorial ? car.age_restrictions : raptorPolicyMinAge;
    return minAge;
  }

  getTravelActivityPacks(extras: any) {
    const activityPack = [];
    if (extras && Object.keys(extras).length > 0) {
      for (const [key, value] of Object.entries(extras)) {
        if (['snow', 'moped', 'cruise', 'adventure', 'gadget', 'flight_delay'].includes(key) && (value as IExtraInterface).is_included) {
          activityPack.push(extras[key].label);
        }
      }
    }

    return activityPack;
  }

  getDestination(policy: any) {
    const destinationCount = policy.human.travel_destination?.length;
    const destinationOne = destinationCount > 0 ? policy.human.travel_destination[0] : '';
    let destinationTitle = '';

    if (policy.product.slug === 'avs-travel-multi-25') {
      destinationTitle = 'My Wanda Plus'
      return destinationTitle;
    }

    if (policy.data.cover_type === 'multi') {
      if (policy.data.travel_region === 'europe') {
        destinationTitle = 'Europe';
      } else if (policy.data.travel_region === 'worldwide') {
        destinationTitle = 'Worldwide';
        if (policy.data.travel_region_worldwide_type === 'worldwide_inc_na') {
          destinationTitle += ' (including USA, Canada, Bermuda, Caribbean Islands & Mexico)';
        } else if (policy.data.travel_region_worldwide_type === 'worldwide_exc_na') {
          destinationTitle += ' (excluding USA, Canada, Bermuda, Caribbean Islands & Mexico)';
        }
      }
    } else {
      destinationTitle = destinationCount > 1 ?
        `${destinationOne} + ${destinationCount - 1} other countr${destinationCount === 2 ? 'y' : 'ies'}` :
        destinationOne;
    }

    return destinationTitle;
  }

  getTravelPolicyType(policy: any) {
    let policyTypeLabel = '';

    if (policy.data.cover_type === 'multi') {
      policyTypeLabel = 'Annual Multi-Trip ';
    } else if (policy.data.cover_type === 'int') {
      policyTypeLabel = 'Single Trip ';
    } else {
      policyTypeLabel = policy.human.cover;
    }

    return policyTypeLabel;
  }

  getLegacyCarCoverLevel(policy: any) {
    const coverLevels = CAR_CONSTANTS.legacyCoverLevels as any;

    return coverLevels[policy.policy_type];
  }

  getLegacyCarCoverType(policy: any) {
    const coverTypes = CAR_CONSTANTS.legacyCoverTypes as any;

    return coverTypes[policy.policy_type];
  }

  retryPolicyPayment(policySlug: string) {
    return this.policyService.retryPaymentByPolicySlug(policySlug);
  }

  getProductIcon(productClass: 'car' | 'home' | 'travel' | 'mobile' | 'contents' | '') {
    const icon = {
      car: 'options-hirecar-small',
      home: 'house-home-small',
      travel: 'travel-small',
      mobile: 'marketing-optionals',
      contents: 'stuff-landlordscontents'
    };

    return productClass ? icon[productClass] : '';
  }

  getExcessDetails(policy: any, excessList: Record<string, Excess>) {
    const excessDetails: ExcessDetail[] = []; 
    
    Object.entries(excessList).forEach(([key, excess]) => {
      const excessPrice = this.getPriceObject(policy, policy.data[excess.key])
      excessDetails.push({
        name: excess.name || '',
        summary: `${excessPrice.currency} ${excessPrice.amount}` || ''
      });
    });
    
    return excessDetails;
  }

  getExcessConfig(validateFlag: boolean) {
    const policyMobileExcessConfig = POLICY_DETAIL_CONFIG.policyMobileExcessConfig as IConfigInterface;
    if (validateFlag) {
      policyMobileExcessConfig.footnote = '';
    }
    
    return policyMobileExcessConfig;
  }
}
